import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { showWarning } from 'src/utils/toast/toastUtil';
import { defaultHRM3110_R01Dto, HRM3110_R01Dto } from './Dto_HRM3110_R01';
import { HRM3110_R01GridType } from './Util_HRM3110_R01';

/** 서버 목록·건수·Excel 조회에 공통으로 전달하는 화면 검색 조건이다. */
export interface HRM3110_R01Filter {
    allowDeductType: string; // 구분(A 지급, D 공제). 빈 값이면 전체
    taxType: string; // 과세구분(T, N, -). 빈 값이면 전체
    useYn: string; // 사용여부(Y/N). 빈 값이면 전체
}
/** 빈 문자열은 해당 검색 조건을 적용하지 않는 기본 상태를 뜻한다. */
export const defaultHRM3110_R01Filter: HRM3110_R01Filter = { allowDeductType: '', taxType: '', useYn: '' };

/**
 * 지급·공제 두 그리드의 행, 선택·삭제 표시, 검색 조건 및 그리드 표시 상태를 관리한다.
 * 두 그리드는 Header/Detail 관계가 아니라 같은 테이블을 구분(A/D)으로 나눈 나란한 목록이므로 상태를 그리드별로 따로 둔다.
 */
export interface Store_HRM3110_R01 {
    HRM3110_R01Filter: HRM3110_R01Filter;
    HRM3110_R01PastFilter: HRM3110_R01Filter;
    setHRM3110_R01Filter: (filter: Partial<HRM3110_R01Filter>) => void;
    setHRM3110_R01PastFilter: (filter: Partial<HRM3110_R01Filter>) => void;

    activeGridType: HRM3110_R01GridType; // 마지막으로 클릭한 그리드. 등록·삭제 버튼의 대상
    setActiveGridType: (gridType: HRM3110_R01GridType) => void;

    HRM3110_R01AllowList: HRM3110_R01Dto[];
    setHRM3110_R01AllowList: (list: HRM3110_R01Dto[]) => void;
    insertHRM3110_R01AllowList: (data: HRM3110_R01Dto[], index?: number) => void;
    removeHRM3110_R01AllowList: (index: number) => void;
    updateHRM3110_R01AllowList: (index: number, data: HRM3110_R01Dto) => void;
    selectedHRM3110_R01AllowList: string[];
    addSelectedHRM3110_R01AllowList: (ids: string[]) => void;
    removeSelectedHRM3110_R01AllowList: (ids?: string[]) => void;
    initSelectedHRM3110_R01AllowList: () => void;
    allSelectedHRM3110_R01AllowList: () => void;
    deletedHRM3110_R01AllowList: string[];
    addDeletedHRM3110_R01AllowList: (ids: string[]) => void;
    removeDeletedHRM3110_R01AllowList: (ids?: string[]) => void;
    originalHRM3110_R01AllowList: HRM3110_R01Dto[];
    setOriginalHRM3110_R01AllowList: (list: HRM3110_R01Dto[]) => void;

    HRM3110_R01DeductList: HRM3110_R01Dto[];
    setHRM3110_R01DeductList: (list: HRM3110_R01Dto[]) => void;
    insertHRM3110_R01DeductList: (data: HRM3110_R01Dto[], index?: number) => void;
    removeHRM3110_R01DeductList: (index: number) => void;
    updateHRM3110_R01DeductList: (index: number, data: HRM3110_R01Dto) => void;
    selectedHRM3110_R01DeductList: string[];
    addSelectedHRM3110_R01DeductList: (ids: string[]) => void;
    removeSelectedHRM3110_R01DeductList: (ids?: string[]) => void;
    initSelectedHRM3110_R01DeductList: () => void;
    allSelectedHRM3110_R01DeductList: () => void;
    deletedHRM3110_R01DeductList: string[];
    addDeletedHRM3110_R01DeductList: (ids: string[]) => void;
    removeDeletedHRM3110_R01DeductList: (ids?: string[]) => void;
    originalHRM3110_R01DeductList: HRM3110_R01Dto[];
    setOriginalHRM3110_R01DeductList: (list: HRM3110_R01Dto[]) => void;

    allowColumnState: ColumnState[] | null;
    setAllowColumnState: (state: ColumnState[] | null) => void;
    deductColumnState: ColumnState[] | null;
    setDeductColumnState: (state: ColumnState[] | null) => void;
    allowScrollTop: number | null;
    setAllowScrollTop: (top: number | null) => void;
    deductScrollTop: number | null;
    setDeductScrollTop: (top: number | null) => void;
    allCount: number;
    setAllCount: (count: number) => void;
    initData: () => void;
}

/**
 * 서버 행의 누락된 화면 기본값과 그리드 식별·정렬 인덱스를 채운다. 두 그리드 목록이 같은 규칙을 쓴다.
 * @param list 서버 응답 또는 화면 목록
 * @returns valueIndex·orderIndex가 채워진 새 목록. 이미 부여된 값은 유지하고, 없으면 1부터 부여한다.
 */
const buildGridList = (list: HRM3110_R01Dto[]): HRM3110_R01Dto[] => list.map((item, index) => ({
    ...defaultHRM3110_R01Dto, ...item,
    valueIndex: item.valueIndex > 0 ? item.valueIndex : index + 1,
    orderIndex: item.orderIndex > 0 ? item.orderIndex : index + 1,
    orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
}));

/**
 * 서버 조회 원본에 안정적인 인덱스를 부여한다. 변경 비교용 목록에 쓴다.
 * @param list 서버 응답 목록
 * @returns valueIndex·orderIndex를 1부터 다시 매긴 새 목록
 */
const buildOriginalList = (list: HRM3110_R01Dto[]): HRM3110_R01Dto[] => list.map((item, index) => ({
    ...defaultHRM3110_R01Dto, ...item,
    valueIndex: index + 1, orderIndex: index + 1, orderIndexSeq: 0,
}));

/**
 * 신규 행에 그리드 안에서 고유한 valueIndex와 기준 행의 정렬 인덱스를 부여해 삽입한다.
 * index가 있으면 해당 행 다음에 삽입한다. 기준 행을 찾지 못하면 맨 앞에, index가 없으면 맨 뒤에 넣는다.
 * @param currentList 현재 그리드 목록
 * @param data 삽입할 행 목록
 * @param index 기준 행의 valueIndex
 * @returns 신규 행이 들어간 새 목록
 */
const insertGridRows = (currentList: HRM3110_R01Dto[], data: HRM3110_R01Dto[], index?: number): HRM3110_R01Dto[] => {
    const list = [...currentList];
    const target = list.find(item => item.valueIndex === index);
    // ??: 기준 행이 없으면 정렬 인덱스를 0부터 시작한다.
    const orderIndex = target?.orderIndex ?? 0;
    const orderIndexSeq = (target?.orderIndexSeq ?? 0) + 1;
    const maxIndex = Math.max(0, ...list.map(item => item.valueIndex));
    const newRows = data.map((item, offset) => ({
        ...defaultHRM3110_R01Dto, ...item,
        valueIndex: maxIndex + offset + 1,
        orderIndex,
        orderIndexSeq,
    }));
    // + 버튼 또는 등록 버튼에서 기준 행이 전달되면 해당 행 바로 뒤에 삽입한다.
    if (index !== undefined) {
        list.splice(Math.max(0, list.findIndex(item => item.valueIndex === index) + 1), 0, ...newRows);
    } else {
        // 기준 행이 없으면 신규 행을 목록 끝에 추가한다.
        list.push(...newRows);
    }
    return list;
};

/**
 * 해당 valueIndex 행을 교체한다. 음수 순서·비과세한도는 경고 후 0으로 보정한다.
 * @param currentList 현재 그리드 목록
 * @param index 교체할 행의 valueIndex
 * @param data 새 행 데이터
 * @returns 행이 교체된 새 목록
 */
const updateGridRow = (currentList: HRM3110_R01Dto[], index: number, data: HRM3110_R01Dto): HRM3110_R01Dto[] => {
    const updated = { ...data };
    // 음수 순서를 그대로 Store에 보관하지 않는다.
    if (updated.sortSeq < 0) {
        showWarning('순서는 0보다 작을 수 없습니다.');
        updated.sortSeq = 0;
    }
    // 음수 비과세한도를 그대로 Store에 보관하지 않는다.
    if (updated.nontaxLimit < 0) {
        showWarning('비과세한도는 0보다 작을 수 없습니다.');
        updated.nontaxLimit = 0;
    }
    return currentList.map(item => item.valueIndex === index ? updated : item);
};

/**
 * 식별값 목록에서 지정한 값만 지우거나, ids가 없으면 전체를 비운다.
 * @param currentIds 현재 선택 또는 삭제 식별값 목록
 * @param ids 지울 식별값. 없으면 전체 삭제
 * @returns 남은 식별값 목록
 */
const removeIds = (currentIds: string[], ids?: string[]) => currentIds.filter(id => ids ? !ids.includes(id) : false);

/**
 * HRM3110_R01 화면 전용 Store이다. 각 action은 반환값 없이 지정한 상태만 갱신한다.
 * 조회·저장 API 호출은 Content에서 수행한다.
 */
export const useHRM3110_R01Store = create<Store_HRM3110_R01>((set) => ({
    HRM3110_R01Filter: { ...defaultHRM3110_R01Filter },
    HRM3110_R01PastFilter: { ...defaultHRM3110_R01Filter },
    /** 전달된 검색 필드만 현재 조건에 병합한다. */
    setHRM3110_R01Filter: (filter) => set(state => ({ HRM3110_R01Filter: { ...state.HRM3110_R01Filter, ...filter } })),
    /** 마지막 조회에 사용한 검색 조건을 부분 갱신한다. */
    setHRM3110_R01PastFilter: (filter) => set(state => ({ HRM3110_R01PastFilter: { ...state.HRM3110_R01PastFilter, ...filter } })),

    activeGridType: 'allow',
    /**
     * 등록·삭제 버튼이 적용될 그리드를 바꾼다. 값이 같으면 기존 상태를 그대로 돌려줘 화면을 다시 그리지 않는다.
     * 그리드 영역을 클릭할 때마다 호출되므로, 매번 다시 그리면 그리드 컬럼 정의가 새로 만들어져 셀 편집이 끊길 수 있다.
     */
    // 삼항 연산자: 같은 그리드면 현재 상태 객체를 그대로, 다르면 바뀐 값만 반환한다.
    setActiveGridType: (activeGridType) => set(state => state.activeGridType === activeGridType ? state : { activeGridType }),

    HRM3110_R01AllowList: [],
    /** 지급 그리드 목록을 인덱스를 채워 저장한다. */
    setHRM3110_R01AllowList: (list) => set({ HRM3110_R01AllowList: buildGridList(list) }),
    /** 지급 그리드에 신규 행을 삽입한다. */
    insertHRM3110_R01AllowList: (data, index) => set(state => ({
        HRM3110_R01AllowList: insertGridRows(state.HRM3110_R01AllowList, data, index),
    })),
    /** valueIndex로 지급 신규 행을 제거하고 선택·삭제 목록에서도 같은 식별값을 지운다. */
    removeHRM3110_R01AllowList: (index) => set(state => ({
        HRM3110_R01AllowList: state.HRM3110_R01AllowList.filter(item => item.valueIndex !== index),
        // 목록에 숫자와 문자열 식별값이 섞일 수 있어 양쪽을 문자열로 맞춰 비교한다.
        selectedHRM3110_R01AllowList: state.selectedHRM3110_R01AllowList.filter(id => String(id) !== String(index)),
        deletedHRM3110_R01AllowList: state.deletedHRM3110_R01AllowList.filter(id => String(id) !== String(index)),
    })),
    /** 지급 그리드의 해당 valueIndex 행을 교체한다. */
    updateHRM3110_R01AllowList: (index, data) => set(state => ({
        HRM3110_R01AllowList: updateGridRow(state.HRM3110_R01AllowList, index, data),
    })),
    selectedHRM3110_R01AllowList: [],
    /** 지급 그리드의 새 선택 식별값을 중복 없이 합친다. */
    addSelectedHRM3110_R01AllowList: (ids) => set(state => ({
        selectedHRM3110_R01AllowList: Array.from(new Set([...state.selectedHRM3110_R01AllowList, ...ids])),
    })),
    /** ids가 있으면 해당 선택만 제거하고, 없으면 지급 그리드 선택을 모두 해제한다. */
    removeSelectedHRM3110_R01AllowList: (ids) => set(state => ({
        selectedHRM3110_R01AllowList: removeIds(state.selectedHRM3110_R01AllowList, ids),
    })),
    /** 지급 그리드 선택 목록을 빈 배열로 초기화한다. */
    initSelectedHRM3110_R01AllowList: () => set({ selectedHRM3110_R01AllowList: [] }),
    /** 지급 그리드의 모든 valueIndex를 문자열 식별값으로 선택한다. */
    allSelectedHRM3110_R01AllowList: () => set(state => ({
        selectedHRM3110_R01AllowList: state.HRM3110_R01AllowList.map(item => String(item.valueIndex)),
    })),
    deletedHRM3110_R01AllowList: [],
    /** 저장 시 삭제할 지급 기존 행의 식별값을 중복 없이 추가한다. */
    addDeletedHRM3110_R01AllowList: (ids) => set(state => ({
        deletedHRM3110_R01AllowList: Array.from(new Set([...state.deletedHRM3110_R01AllowList, ...ids])),
    })),
    /** ids가 있으면 지정된 삭제 표시만, 없으면 지급 그리드 삭제 표시 전체를 지운다. */
    removeDeletedHRM3110_R01AllowList: (ids) => set(state => ({
        deletedHRM3110_R01AllowList: removeIds(state.deletedHRM3110_R01AllowList, ids),
    })),
    originalHRM3110_R01AllowList: [],
    /** 지급 그리드의 서버 조회 원본을 변경 비교용으로 저장한다. */
    setOriginalHRM3110_R01AllowList: (list) => set({ originalHRM3110_R01AllowList: buildOriginalList(list) }),

    HRM3110_R01DeductList: [],
    /** 공제 그리드 목록을 인덱스를 채워 저장한다. */
    setHRM3110_R01DeductList: (list) => set({ HRM3110_R01DeductList: buildGridList(list) }),
    /** 공제 그리드에 신규 행을 삽입한다. */
    insertHRM3110_R01DeductList: (data, index) => set(state => ({
        HRM3110_R01DeductList: insertGridRows(state.HRM3110_R01DeductList, data, index),
    })),
    /** valueIndex로 공제 신규 행을 제거하고 선택·삭제 목록에서도 같은 식별값을 지운다. */
    removeHRM3110_R01DeductList: (index) => set(state => ({
        HRM3110_R01DeductList: state.HRM3110_R01DeductList.filter(item => item.valueIndex !== index),
        selectedHRM3110_R01DeductList: state.selectedHRM3110_R01DeductList.filter(id => String(id) !== String(index)),
        deletedHRM3110_R01DeductList: state.deletedHRM3110_R01DeductList.filter(id => String(id) !== String(index)),
    })),
    /** 공제 그리드의 해당 valueIndex 행을 교체한다. */
    updateHRM3110_R01DeductList: (index, data) => set(state => ({
        HRM3110_R01DeductList: updateGridRow(state.HRM3110_R01DeductList, index, data),
    })),
    selectedHRM3110_R01DeductList: [],
    /** 공제 그리드의 새 선택 식별값을 중복 없이 합친다. */
    addSelectedHRM3110_R01DeductList: (ids) => set(state => ({
        selectedHRM3110_R01DeductList: Array.from(new Set([...state.selectedHRM3110_R01DeductList, ...ids])),
    })),
    /** ids가 있으면 해당 선택만 제거하고, 없으면 공제 그리드 선택을 모두 해제한다. */
    removeSelectedHRM3110_R01DeductList: (ids) => set(state => ({
        selectedHRM3110_R01DeductList: removeIds(state.selectedHRM3110_R01DeductList, ids),
    })),
    /** 공제 그리드 선택 목록을 빈 배열로 초기화한다. */
    initSelectedHRM3110_R01DeductList: () => set({ selectedHRM3110_R01DeductList: [] }),
    /** 공제 그리드의 모든 valueIndex를 문자열 식별값으로 선택한다. */
    allSelectedHRM3110_R01DeductList: () => set(state => ({
        selectedHRM3110_R01DeductList: state.HRM3110_R01DeductList.map(item => String(item.valueIndex)),
    })),
    deletedHRM3110_R01DeductList: [],
    /** 저장 시 삭제할 공제 기존 행의 식별값을 중복 없이 추가한다. */
    addDeletedHRM3110_R01DeductList: (ids) => set(state => ({
        deletedHRM3110_R01DeductList: Array.from(new Set([...state.deletedHRM3110_R01DeductList, ...ids])),
    })),
    /** ids가 있으면 지정된 삭제 표시만, 없으면 공제 그리드 삭제 표시 전체를 지운다. */
    removeDeletedHRM3110_R01DeductList: (ids) => set(state => ({
        deletedHRM3110_R01DeductList: removeIds(state.deletedHRM3110_R01DeductList, ids),
    })),
    originalHRM3110_R01DeductList: [],
    /** 공제 그리드의 서버 조회 원본을 변경 비교용으로 저장한다. */
    setOriginalHRM3110_R01DeductList: (list) => set({ originalHRM3110_R01DeductList: buildOriginalList(list) }),

    allowColumnState: null,
    /** 지급 그리드 컬럼 순서·너비·표시 상태를 저장한다. */
    setAllowColumnState: (allowColumnState) => set({ allowColumnState }),
    deductColumnState: null,
    /** 공제 그리드 컬럼 순서·너비·표시 상태를 저장한다. */
    setDeductColumnState: (deductColumnState) => set({ deductColumnState }),
    allowScrollTop: null,
    /** 조회 또는 탭 복귀에 사용할 지급 그리드 스크롤 위치를 저장한다. */
    setAllowScrollTop: (allowScrollTop) => set({ allowScrollTop }),
    deductScrollTop: null,
    /** 조회 또는 탭 복귀에 사용할 공제 그리드 스크롤 위치를 저장한다. */
    setDeductScrollTop: (deductScrollTop) => set({ deductScrollTop }),
    allCount: 0,
    /** 현재 검색 조건에 맞는 서버 전체 건수(지급 + 공제)를 저장한다. */
    setAllCount: (allCount) => set({ allCount }),
    /** 필터, 두 그리드의 목록·선택·삭제·원본, 대상 그리드, 컬럼 상태, 스크롤, 건수를 초기화한다. 콤보 목록은 공통 Store에서 관리한다. */
    initData: () => set({
        HRM3110_R01Filter: { ...defaultHRM3110_R01Filter },
        HRM3110_R01PastFilter: { ...defaultHRM3110_R01Filter },
        activeGridType: 'allow',
        HRM3110_R01AllowList: [],
        selectedHRM3110_R01AllowList: [],
        deletedHRM3110_R01AllowList: [],
        originalHRM3110_R01AllowList: [],
        HRM3110_R01DeductList: [],
        selectedHRM3110_R01DeductList: [],
        deletedHRM3110_R01DeductList: [],
        originalHRM3110_R01DeductList: [],
        allowColumnState: null,
        deductColumnState: null,
        allowScrollTop: null,
        deductScrollTop: null,
        allCount: 0,
    }),
}));
